import { useEffect, useState, type MouseEvent } from 'react'
import type { SubTemplateProps } from '@/deskUiSkeletons/rowTemplates/RowTemplateTypes'

function isMailboxPreviewOpen() {
  return typeof window !== 'undefined' && Boolean((window as any).__MSGDESK_MAILBOX_PREVIEW_OPEN__)
}

function subHref(display: SubTemplateProps['display'], sub: SubTemplateProps['sub']) {
  const href = display.href || sub.href || ''
  return href && href !== '#' ? href : ''
}

function shouldIgnorePreviewClick(target: EventTarget | null) {
  return target instanceof Element && Boolean(target.closest('button,input,textarea,select,[role="button"],.md-sub-icon,.md-sub-meta-link,.md-sub-meta-more'))
}

function initial(value: string) {
  return value.trim().slice(0, 1).toUpperCase() || 'M'
}

export function MailboxHotmailTemplate({ sub, display, controls, slots }: SubTemplateProps) {
  const [isSelected, setIsSelected] = useState(false)
  const isRead = display.state.isRead
  const iconLetter = initial(display.identity.author || display.title || display.codeKey || sub.codeKey || '')

  useEffect(() => {
    function handlePreviewSub(event: Event) {
      const elId = Number((event as CustomEvent<{ elId?: number }>).detail?.elId || 0)
      setIsSelected(elId === display.elId)
    }

    window.addEventListener('msgdesk:mailbox-preview-sub', handlePreviewSub)
    return () => window.removeEventListener('msgdesk:mailbox-preview-sub', handlePreviewSub)
  }, [display.elId])

  function dispatchPreviewSub() {
    window.dispatchEvent(new CustomEvent('msgdesk:mailbox-preview-sub', { detail: { elId: display.elId } }))
  }

  function handleRowClickCapture(event: MouseEvent<HTMLDivElement>) {
    if (!isMailboxPreviewOpen() || shouldIgnorePreviewClick(event.target)) return
    event.preventDefault()
    dispatchPreviewSub()
  }

  function handleRowClick(event: MouseEvent<HTMLDivElement>) {
    if (event.defaultPrevented) return

    if (isMailboxPreviewOpen()) {
      event.preventDefault()
      if (!shouldIgnorePreviewClick(event.target)) dispatchPreviewSub()
      return
    }

    const href = subHref(display, sub)
    if (!href) return
    const target = event.target instanceof Element ? event.target : null
    if (target?.closest('a,button,input,textarea,select,[role="button"],.md-sub-icon,.md-sub-meta-link,.md-sub-meta-more')) return
    window.location.href = href
  }

  return (
    <>
      <div
        className={`md-subs-hotmail-row group flex min-h-[68px] cursor-pointer items-stretch border-b border-[#e6ebf0] bg-white pr-3 text-[13px] text-[#323130] font-['Segoe_UI',Tahoma,Geneva,Verdana,sans-serif] transition-colors hover:bg-[#f6f9fc]${display.isChecked ? ' md-subs-hotmail-row--checked bg-[#deecf9]' : ''}${isSelected ? ' md-subs-hotmail-row--selected bg-[#edf5ff] shadow-[inset_3px_0_0_#2563eb]' : ''}`}
        onClickCapture={handleRowClickCapture}
        onClick={handleRowClick}
      >
        <div className={`md-subs-hotmail-accent w-1 flex-none ${isSelected ? 'bg-[#2563eb]' : display.isChecked ? 'bg-[#0078d4]' : 'bg-transparent'}`} />
        <div className="md-subs-hotmail-icons flex w-14 flex-none items-center justify-center gap-1">
          <div className={`relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border text-[12px] font-semibold shadow-sm ${display.isChecked ? 'border-[#0078d4] bg-[#cfe8ff] text-[#1556a8]' : isSelected ? 'border-[#93c5fd] bg-white text-[#1556a8]' : 'border-[#c8d2dc] bg-[#f8fafc] text-[#526272] group-hover:bg-white'} [&_.md-sub-icon--check-control]:absolute [&_.md-sub-icon--check-control]:inset-0 [&_.md-sub-icon--check-control]:h-full [&_.md-sub-icon--check-control]:w-full [&_.md-sub-icon--check-control]:rounded-full [&_.md-sub-icon--check-control]:border-0 [&_.md-sub-icon--check-control]:bg-transparent [&_.md-sub-icon--check-control]:text-transparent`}>
            <span className="absolute inset-0 flex items-center justify-center">
              {display.media.avatarPath ? (
                <img
                  src={display.media.avatarPath}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={(event) => { event.currentTarget.style.display = 'none' }}
                />
              ) : (
                <span>{iconLetter}</span>
              )}
            </span>
            {display.isChecked ? <span className="absolute bottom-0 right-0 h-4 w-4 rounded-full border-2 border-white bg-[#2563eb]" /> : null}
            <span className="absolute inset-0">{controls.actions.check}</span>
          </div>
        </div>
        <div className="md-subs-hotmail-main flex min-w-0 flex-1 flex-col justify-center py-2.5">
          <div className="md-subs-hotmail-line flex min-w-0 items-start gap-3">
            <div className={`md-subs-hotmail-title min-w-[140px] flex-[0_1_220px] overflow-hidden text-ellipsis whitespace-nowrap ${isRead ? 'font-medium text-[#3b4652]' : 'font-semibold text-[#17212b]'} [&_.md-sub-title-link]:text-inherit`}>
              {controls.content.title}
            </div>
            <div className={`md-subs-hotmail-text min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap ${isRead ? 'text-[#687684]' : 'text-[#344252]'}`}>
              {controls.content.text}
            </div>
            <div className={`md-subs-hotmail-time flex-none text-xs tabular-nums ${isSelected ? 'text-[#1556a8]' : 'text-[#687684]'}`}>{controls.content.time}</div>
          </div>
          <div className="md-subs-hotmail-meta mt-1.5 flex min-h-5 items-center gap-3 text-xs text-[#2563eb]">
            <span className="flex min-w-0 flex-1 items-center gap-3">
              {controls.links.answers}
              {controls.links.submsges}
              {controls.actions.quickReply}
            </span>
            <span className="flex flex-none items-center gap-2 opacity-80 transition-opacity group-hover:opacity-100">
              {controls.actions.favorite}
            </span>
          </div>
        </div>
      </div>
      {slots.allChildren}
    </>
  )
}
